import {
  Component,
  Host,
  ViewChild,
  ElementRef,
  Inject
} from '@angular/core';

import { NgClass, AsyncPipe } from '@angular/common';
import { DateTimeFormat, DateFormatterService } from '../../../services/date/date-formatter.service';

import { PipesModule } from '../../../pipes/pipes.module';
import { IconComponent } from '../../icon/icon.component';
import { FocusOnDisabledDirective } from '../../focus/focus-on-disabled.directive';
import { DatePickerButtonComponent } from '../date-picker-button/date-picker-button.component';
import { DatePickerInnerComponent } from '../date-picker-inner/date-picker-inner.component';
import type { DateObject } from '../date-picker-inner/date-picker-inner.domain';
import type { OnInit } from '@angular/core';

/**
 * Label for dates
 */
interface Label {
  abbr: string;
  full: string;
}

/**
 * The Day Picker Component
 *
 * This is the day view of the date picker component
 */
@Component({
  selector: 'td-wb-day-picker',
  templateUrl: './day-picker.component.html',
  styleUrls: ['./day-picker.component.scss'],
  imports: [DatePickerButtonComponent, FocusOnDisabledDirective, IconComponent, NgClass, AsyncPipe, PipesModule]
})
export class DayPickerComponent implements OnInit {

  // Number of days on a six-week calendar
  private static readonly NUM_DAYS: number = 42;

  @ViewChild('yearButton', { static: false, read: ElementRef }) yearButtonRef: ElementRef;

  /**
   * Array of labels
   */
  labels: Array<Label> = [];

  /**
   * Title of day view
   */
  title: string;

  /**
   * Array of dates to display in the view
   */
  rows: Array<Array<DateObject>> = [];

  /**
   * Array of week numbers
   */
  weekNumbers: Array<number> = [];

  /**
   * Parent DatePickerInnerComponent
   */
  datePicker: DatePickerInnerComponent;

  /**
   * Boolean for determining whether previous button should be disabled
   */
  navPrevDisabled: boolean;

  /**
   * Boolean for determining whether next button should be disabled
   */
  navNextDisabled: boolean;

  constructor(@Inject(DatePickerInnerComponent) @Host() datePicker: DatePickerInnerComponent,
              @Inject(DateFormatterService) private dateFormatterService: DateFormatterService) {
    this.datePicker = datePicker;
  }

  /**
   * This function registers handlers and initializes step
   */
  ngOnInit(): void {
    this.datePicker.step.set('day', { months: 1 });
    this.datePicker.setRefreshViewHandler((): void => this.refreshView(), 'day');
    this.datePicker.setCompareHandler((date1: Date, date2: Date): number => this.compareDates(date1, date2), 'day');
    this.datePicker.refreshView();
  }

  /**
   * This function is the refresh view handler for date picker inner component
   */
  refreshView(): void {
    const datePicker = this.datePicker;
    const year = datePicker.activeDate.getFullYear();
    const month = datePicker.activeDate.getMonth();
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const difference = 0 - firstDayOfMonth.getDay();
    const numDisplayedFromPreviousMonth = difference > 0 ? 7 - difference : -difference;
    const firstDate = new Date(firstDayOfMonth.getTime());

    if (numDisplayedFromPreviousMonth > 0) {
      firstDate.setDate(-numDisplayedFromPreviousMonth + 1);
    }

    const dates: Array<Date> = this.getDates(firstDate, DayPickerComponent.NUM_DAYS);
    const days: Array<DateObject> = this.generateDateObjects(dates, month);

    if (datePicker.displayDatesOfCurrentMontyOnly) {
      this.setDateObjectsInvisible(days, firstDayOfMonth, lastDayOfMonth);
    }

    Object.assign(this, this.setBtnDisable(firstDayOfMonth));
    this.labels = this.getLabels(days);
    this.title = datePicker.formatDate(datePicker.activeDate, datePicker.formatDayTitle);
    this.rows = datePicker.split(days, 7) as Array<Array<DateObject>>;
    this.weekNumbers = datePicker.showWeeks ? this.setWeekNumbers() : undefined;
  }

  /**
   * This function is the comparator for date picker inner component
   *
   * @param date1 Date to compare
   * @param date2 Date to compare
   * @returns Returns comparison of two dates
   */
  compareDates(date1: Date, date2: Date): number {
    const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
    const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());
    return d1.getTime() - d2.getTime();
  }

  getAriaLabel(date: Date): string {
    return this.dateFormatterService.formatDate(date, DateTimeFormat.LONG_DATE);
  }

  /**
   * @param startDate first start date of date list
   * @param n Number of dates to get
   * @returns Returns array of dates of length n
   */
  private getDates(startDate: Date, n: number): Array<Date> {
    const dates: Array<Date> = new Array(n);
    let current = new Date(startDate.getTime());
    let i = 0;
    let date: Date;
    while (i < n) {
      date = new Date(current.getTime());
      dates[i++] = date;
      current = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate() + 1
      );
    }
    return dates;
  }

  /**
   * This function converts array of Date to array of DateObject.
   *
   * @param _days Array of Date
   * @param month Current active date's month
   * @returns Returns array of DateObject
   */
  private generateDateObjects(_days: Array<Date>, month: number): Array<DateObject> {
    const datePicker = this.datePicker;
    const days: Array<DateObject> = [];
    for (let i = 0; i < DayPickerComponent.NUM_DAYS; i++) {
      const dateObject = datePicker.createDateObject(_days[i], datePicker.formatDay);
      dateObject.secondary = _days[i].getMonth() !== month;
      dateObject.uid = datePicker.uniqueId + '-' + i;
      dateObject.isVisible = true;
      days[i] = dateObject;
    }
    return days;
  }

  /**
   * This function is to set DateObjects of previous and next months to be invisible.
   *
   * @param days Array of DateObject
   * @param firstDayOfMonth First Day of the month
   * @param firstDayOfMonth Last Day of the month
   */
  private setDateObjectsInvisible(days: Array<DateObject>, firstDayOfMonth: Date, lastDayOfMonth: Date): void {
    for (let i = 0; i < DayPickerComponent.NUM_DAYS; i++) {
      if (days[i].date < firstDayOfMonth || days[i].date > lastDayOfMonth) {
        days[i].isVisible = false;
      }
    }
  }

  /**
   * This function handles disabling states of previous/next buttons
   *
   * @param firstDayOfMonth First day of the month
   * @returns Disabled states
   */
  private setBtnDisable(firstDayOfMonth: Date): unknown {
    const datePicker = this.datePicker;
    const disabled: unknown = {};
    const calendarMinDate = new Date(firstDayOfMonth.getTime());
    calendarMinDate.setHours(0);
    disabled['navPrevDisabled'] = (datePicker.minDate !== undefined) && (calendarMinDate <= datePicker.minDate);
    const calendarMaxDate = new Date(firstDayOfMonth.getTime());
    calendarMaxDate.setHours(0);
    calendarMaxDate.setMonth(calendarMaxDate.getMonth() + 1);
    // if the first day of the next month is greater then maxDate there is no point to navigate to the next month
    disabled['navNextDisabled'] = calendarMaxDate > datePicker.maxDate;
    return disabled;
  }

  /**
   * This function retrieves labels from array of DateObject
   *
   * @param days Array of DateObject
   * @returns Returns array of Label
   */
  private getLabels(days: Array<DateObject>): Array<Label> {
    const datePicker = this.datePicker;
    const labels: Array<Label> = [];
    for (let j = 0; j < 7; j++) {
      const label: Label = { abbr: undefined, full: undefined };
      label.abbr = datePicker.formatDate(
        days[j].date,
        datePicker.formatDayHeader
      );
      label.full = datePicker.formatDate(days[j].date, DateTimeFormat.DAY_OF_WEEK_FULL);
      labels.push(label);
    }
    return labels;
  }

  /**
   * This function retrieves week numbers
   *
   * @returns Returns week numbers
   */
  private setWeekNumbers(): Array<number> {
    const datePicker = this.datePicker;
    const weekNumbers: Array<number> = [];
    const thursdayIndex = (4 + 7 - datePicker.startingDay) % 7;
    const numWeeks = this.rows.length;
    for (let curWeek = 0; curWeek < numWeeks; curWeek++) {
      weekNumbers.push(
        this.getISO8601WeekNumber(this.rows[curWeek][thursdayIndex].date)
      );
    }
    return weekNumbers;
  }

  /**
   * This function returns ISO8601 week number of date
   *
   * @param date Date object
   * @returns ISO8601 week number
   */
  private getISO8601WeekNumber(date: Date): number {
    const checkDate = new Date(date.getTime());
    // Thursday
    let day = checkDate.getDay();
    day = day === 0 ? 7 : day;
    checkDate.setDate(checkDate.getDate() + 4 - day);
    const time = checkDate.getTime();
    // Compare with Jan 1
    checkDate.setMonth(0);
    checkDate.setDate(1);
    return (
      Math.floor(Math.round((time - checkDate.getTime()) / 86400000) / 7) + 1
    );
  }

}
